import React from 'react';
import { cn } from '../../lib/helperClass';

interface buttonInterface extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  variant?:
    | 'primary'
    | 'outline'
    | 'dark'
    | 'info'
    | 'warning'
    | 'danger'
    | 'success';
  disabled?: boolean;
  loading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  id?: string;
  size?: 'sm' | 'md';
  icon?: React.ReactNode;
  'data-test'?: string;
  'data-testid'?: string;
}

export default function Button({
  children,
  variant = 'primary',
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className,
  id,
  size = 'md',
  icon,
  'data-test': dataTest,
  'data-testid': dataTestId,
  form,
  ...rest
}: buttonInterface) {
  /**
   * didn't use (useMemo) here as we don't make expenisve computation to obtain the value of buttonClasses string
   * also this will re-create each render but string is primative types
   * JavaScript engines like V8 (used by Chrome and Node.js) optimize string handling through interning, where identical string literals are reused.
   * ref : https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String
   */
  // const { theme } = useContext(ThemeContext);

  const baseClasses =
    'rounded-xl duration-300 ease-out border border-light-text-secondary flex items-center justify-center gap-2';

  const sizeClasses = {
    md: 'px-4 py-2',
    sm: 'px-2 py-1',
  };

  const variants = {
    primary:
      'bg-light-button-primary text-light-button-label-primary hover:bg-light-button-hover hover:text-light-button-label-hover hover:border-pink-500',
    outline:
      'bg-light-button-primary-outline text-light-text-primary hover:bg-light-button-primary-outline-hover hover:text-light-button-label-hover hover:border-pink-500',
    dark: 'bg-gray-900 text-white border-gray-900 hover:bg-pink-500 hover:border-pink-500 hover:text-black-950',
    warning:
      'text-white bg-light-alert border-0 hover:opacity-80 duration-300 transition',
    danger:
      'text-white bg-light-warning border-0 hover:opacity-80 duration-300 transition',
    success:
      'text-white bg-light-success border-0 hover:opacity-80 duration-300 transition',
    info: 'text-white bg-light-information border-0 hover:opacity-80 duration-300 transition',
  };

  const disabledClasses =
    'bg-light-button-disabled text-light-text-placeholder cursor-not-allowed border border-light-text-placeholder';

  const buttonClasses = cn(
    baseClasses,
    sizeClasses[size],
    disabled || loading ? disabledClasses : variants[variant],
    className
  );

  return (
    <button
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      className={buttonClasses}
      id={id}
      form={form}
      data-test={dataTest}
      data-testid={dataTestId || dataTest}
      {...rest}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {!loading && icon}
      <span className="">{children}</span>
    </button>
  );
}
